import { checkIsErrorToolOutput } from "@repo/app-types/agents/errorToolOutput";
import { checkIsNoResultToolOutput } from "@repo/app-types/agents/noResultToolOutput";
import {
  simulatedCallSpeakerId,
  type PlaybookTestAssertion,
  type PlaybookTestAssertionVerdict,
  type PlaybookTestVerdict,
} from "@repo/app-types/entities/playbookTestCase";
import {
  TeammateAgentBranch,
  type TeammateAgentBranchType,
} from "@repo/app-types/entities/teammateAgent";
import {
  ChatToolBase,
  ChatToolDisclosure,
  type ToolComponent,
} from "@repo/ui/chat/chatToolCall";
import { IconPhoneCall } from "@tabler/icons-react";
import { PlaybookTestVerdictSummary } from "../playbookTestVerdictSummary.js";
import {
  SimulatedCallTranscript,
  type SimulatedCallTranscriptMessage,
} from "./simulatedCallTranscript.js";

/** The parts of a simulate-voice-agent-call call this card reads. */
interface SimulateVoiceAgentCallToolInput {
  branch?: TeammateAgentBranchType;
  scenario?: {
    simulated_customer?: { name?: string; description?: string };
  };
}

interface SimulateVoiceAgentCallToolOutput {
  transcript_fragments: {
    speaker_id: string;
    text_transcript: string;
    start_time: number;
  }[];
  tool_calls: {
    offset_ms: number;
    tool_name: string;
    input: { tool_arguments: Record<string, unknown> };
    output: { content?: string };
  }[];
  assessment: {
    verdict: PlaybookTestVerdict | null;
    assertions: PlaybookTestAssertion[] | null;
    assertion_verdicts: PlaybookTestAssertionVerdict[] | null;
  } | null;
}

export const SimulateVoiceAgentCallTool: ToolComponent<
  SimulateVoiceAgentCallToolInput,
  SimulateVoiceAgentCallToolOutput
> = (props) => {
  const { status, input, output } = props;
  const customer = input.scenario?.simulated_customer;
  const callLabel = `Simulated call${customer?.name ? ` with ${customer.name}` : ""}${input.branch === TeammateAgentBranch.Draft ? " on the draft" : ""}`;

  if (status.type === "running") {
    return <ChatToolBase status={status}>{callLabel}…</ChatToolBase>;
  }
  if (checkIsNoResultToolOutput(output)) {
    return <ChatToolBase status={status}>{output.message}</ChatToolBase>;
  }
  if (!output || checkIsErrorToolOutput(output)) {
    return (
      <ChatToolBase status={status}>
        Could not run the simulated call
      </ChatToolBase>
    );
  }

  const verdict = output.assessment?.verdict;
  return (
    <ChatToolDisclosure
      status={status}
      icon={
        <IconPhoneCall className="mt-0.5 size-3.5 shrink-0 text-indigo-500" />
      }
      label={
        verdict
          ? `${callLabel}: ${verdict.passed ? "passed" : "failed"}`
          : callLabel
      }
    >
      <div className="space-y-2">
        {customer?.description ? (
          <div className="space-y-1">
            <div className="text-[10px] font-medium text-gray-400">
              Customer
            </div>
            <div className="text-xs text-gray-600">{customer.description}</div>
          </div>
        ) : null}
        {verdict ? (
          <PlaybookTestVerdictSummary
            verdict={verdict}
            assertionVerdicts={output.assessment?.assertion_verdicts ?? []}
            assertions={output.assessment?.assertions ?? []}
          />
        ) : null}
        <SimulatedCallTranscript messages={buildTranscriptMessages(output)} />
      </div>
    </ChatToolDisclosure>
  );
};

/** The call's spoken turns and tool calls, in the order they happened. */
function buildTranscriptMessages(
  output: SimulateVoiceAgentCallToolOutput,
): SimulatedCallTranscriptMessage[] {
  const turns = output.transcript_fragments.map((fragment) => ({
    offsetMs: fragment.start_time,
    message: {
      role:
        fragment.speaker_id === simulatedCallSpeakerId.Agent
          ? ("assistant" as const)
          : ("user" as const),
      content: fragment.text_transcript,
      toolInput: null,
    },
  }));
  const toolCalls = output.tool_calls.map((toolCall) => ({
    offsetMs: toolCall.offset_ms,
    message: {
      role: "tool" as const,
      content: toolCall.output.content ?? "",
      toolName: toolCall.tool_name,
      toolInput: JSON.stringify(toolCall.input.tool_arguments),
    },
  }));
  return [...turns, ...toolCalls]
    .sort((a, b) => a.offsetMs - b.offsetMs)
    .map(({ message }) => message);
}
